/**
 * Firebase認証を使用したログイン関連のビジネスロジック
 */
import { getEnvironmentConfig, ENDPOINTS } from '../config/apiEndpoints';
import { csrfTokenService } from './csrfTokenService';

class LoginService {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.config = getEnvironmentConfig();
  }

  /**
   * バックエンドAPI経由でログイン
   * @param {string} email - メールアドレス
   * @param {string} password - パスワード
   * @returns {Promise<Object>} ログイン結果
   */
  async loginWithEmailAndPassword(email, password) {
    try {
      // ログインAPIを定義（CSRFトークンを受け取る）
      const loginApi = async ({ email, password, csrfToken }) => {
        const apiUrl = `${this.config.baseUrl}${ENDPOINTS.AUTH.LOGIN}`;

        // バックエンドの /api/auth/login を呼び出し
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken
          },
          body: JSON.stringify({ email, password }),
          credentials: 'include', // クッキーを含める
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          console.error('[LOGIN SERVICE] Backend login failed:', data.message);
          throw new Error(data.message || 'ログインに失敗しました');
        }

        // バックエンドのレスポンスに合わせて、ユーザー情報を返す
        return {
          success: true,
          user: data.user || {},
          message: data.message
        };
      };

      // CSRFトークン付きでログインを実行
      const result = await csrfTokenService.loginWithCsrfToken(loginApi, { email, password });

      return result;

    } catch (error) {
      console.error('[LOGIN SERVICE] Login failed:', error);

      // エラーメッセージをユーザーフレンドリーに変換
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        throw new Error('サーバーに接続できません。ネットワーク接続を確認してください。');
      }

      throw error;
    }
  }

  /**
   * ログアウト処理（AuthContextで処理されるため、ここでは何もしない）
   */
  async logout() {
    return true;
  }

  /**
   * 現在のユーザー情報を取得
   */
  async getCurrentUser() {
    try {
      if (this.isDevelopment) {
        return null;
      }
      return null;
    } catch (error) {
      console.error('[LOGIN SERVICE] Failed to get current user:', error.message);
      return null;
    }
  }

  /**
   * サービス情報を取得（デバッグ用）
   */
  getServiceInfo() {
    return {
      isDevelopment: this.isDevelopment,
      environment: process.env.NODE_ENV,
      baseUrl: this.config.baseUrl,
      version: '1.0.0'
    };
  }
}

// インスタンス作成関数
export const createLoginService = () => {
  return new LoginService();
};

// デフォルトインスタンス
export const loginService = createLoginService();

// 関数スタイルのエクスポート
export const loginWithEmailAndPassword = (email, password) =>
  loginService.loginWithEmailAndPassword(email, password);

export const logout = () => loginService.logout();

export const getCurrentUser = () => loginService.getCurrentUser();


export default LoginService;
